import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  IconButton,
  FormHelperText,
  Dialog,
  DialogContent,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Tabs,
  Tab,
  Divider,
  GlobalStyles,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  categoryAPI,
  productDetailsAPI,
  groupsAPI,
  productAPI,
} from "../services/api";
import Link from "@mui/material/Link";
import CategoryDialog from "../components/dialogs/CategoryDialog";
import SubcategoryDialog from "../components/dialogs/SubcategoryDialog";
import GroupDialog from "../components/dialogs/GroupDialog";
import { createTextValidation } from "../components/common/TextValidation";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RichTextEditor from "../components/common/RichTextEditor";

// TypeScript interfaces for API responses
interface Category {
  id: string;
  name: string;
  categorystatus: number;
  totalproducts: number;
  totalsubcategories: number;
  createdby: string;
  modifiedby: string | null;
  parentId: string | null;
  deletedAt: string | null;
  created: string;
  modified: string;
  groupcategoryId: string | null;
  masterdetailId: string;
}

interface ProductDetail {
  id: number;
  lable: string;
  data: string;
}

interface Group {
  id: string;
  name: string;
  isprice: boolean;
  noofusers: number;
  categoryId: string | null;
  status: boolean;
  createdby: string;
  modifiedby: string | null;
  deletedAt: string | null;
  created: string;
  modified: string;
  masterdetailId: string;
}

const AddProduct: React.FC = () => {
  // Group selection error state
  const [groupError, setGroupError] = useState<string | null>(null);
  // Image upload error state
  const [imageError, setImageError] = useState<string | null>(null);
  // Custom validation for product name and dynamic product detail fields
  const [productNameError, setProductNameError] = useState("");
  const [productDetailErrors, setProductDetailErrors] = useState<{
    [id: string]: string;
  }>({});
  // Get products from Redux state
  // You may need to adjust the import if useAppSelector is not imported yet
  // import { useAppSelector } from '../store/hooks';
  const products = useSelector((state: any) => state.products.products);
  const navigate = useNavigate();
  const { id: editProductId } = useParams<{ id: string }>();
  const isEditMode = Boolean(editProductId);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [existingImageNames, setExistingImageNames] = useState<string[]>([]);
  const [selectedImage, setSelectedImage] = useState<{
    url: string;
    index: number;
  } | null>(null);
  // Track deleted product media names for edit
  const [deletedProductMedia, setDeletedProductMedia] = useState<string[]>([]);

  // Handler for deleting product media in edit mode
  // const handleDeleteProductMedia = (mediaName: string) => {
  //   // Remove from productmedia array (your existing logic should be here)
  //   // Example: setExistingImageNames(existingImageNames.filter(name => name !== mediaName));
  //   // Track deleted media name
  //   setDeletedProductMedia((prev) => [...prev, mediaName]);
  // };

  // API Data States
  const [activeCategories, setActiveCategories] = useState<Category[]>([]);
  const [groupsList, setGroupsList] = useState<Group[]>([]);
  const [subcategories, setSubcategories] = useState<Category[]>([]);
  const [showSubcategory, setShowSubcategory] = useState(false);

  // Dialog states for add modals
  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [openSubcategoryDialog, setOpenSubcategoryDialog] = useState(false);
  const [openGroupDialog, setOpenGroupDialog] = useState(false);

  // Dynamic product details state - now includes both API and custom fields
  const [productDetailFields, setProductDetailFields] = useState<
    Array<{ id: string; label: string; value: string; isDefault: boolean }>
  >([]);

  // Validation schema - moved inside component to access state
  const schema = yup.object().shape({
    name: createTextValidation({
      title: "Product name",
      minLength: 3,
      maxLength: 100,
    }),
    points: yup
      .string()
      .required("Points are required")
      .matches(/^\d+(\.\d{1,2})?$/, "Points must be a valid number")
      .test("positive-number", "Points must be greater than 0", (value) => {
        if (!value) return false;
        const num = parseFloat(value);
        return num > 0;
      }),
    // Category is now optional
    category: yup.string().notRequired().nullable(),
    subcategory: yup.string().when([], {
      is: () => showSubcategory,
      then: (schema) => schema.required("Subcategory is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    quantity: yup
      .string()
      .required("Quantity is required")
      .matches(/^\d+$/, "Quantity must be a whole number")
      .test("positive-number", "Quantity must be greater than 0", (value) => {
        if (!value) return false;
        const num = parseInt(value);
        return num > 0;
      }),
    minOrderQuantity: yup
      .string()
      .required("Minimum order quantity is required")
      .matches(/^\d+$/, "Minimum order quantity must be a whole number")
      .test(
        "positive-number",
        "Minimum order quantity must be greater than 0",
        (value) => {
          if (!value) return false;
          const num = parseInt(value);
          return num > 0;
        },
      )
      .test(
        "less-than-available",
        "Minimum order quantity cannot be greater than available quantity",
        function (value) {
          const quantity = this.parent.quantity;
          if (value && quantity) {
            return parseInt(value) <= parseInt(quantity);
          }
          return true;
        },
      ),
    // description:  createTextValidation({ title: "Product description", minLength: 10, maxLength: 500,allowDot : true }),
    description: yup
      .string()
      .required("Product description is required")
      .test(
        "min-length-plain-text",
        "Product description must be at least 10 characters",
        (value) => {
          if (!value) return false;
          const plainText = value
            .replace(/<[^>]+>/g, "")
            .replace(/&[a-z]+;/gi, "");
          return plainText.trim().length >= 10;
        },
      )
      .test(
        "max-length-plain-text",
        "Product description cannot exceed 5000 characters",
        (value) => {
          if (!value) return false;
          const plainText = value
            .replace(/<[^>]+>/g, "")
            .replace(/&[a-z]+;/gi, "");
          return plainText.trim().length <= 5000;
        },
      ),
    productDetails: yup.mixed(),
    groupPoints: yup.mixed(),
    productstatus: yup
      .string()
      .oneOf(["1", "0"])
      .required("Product status is required"),
  });

  // Form setup with react-hook-form
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    register,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: "",
      points: "",
      category: "",
      subcategory: "",
      quantity: "",
      minOrderQuantity: "",
      description: "",
      productDetails: {},
      groupPoints: {},
      productstatus: "1",
    },
  });

  // Helper for RichTextEditor image upload
  const uploadDescriptionImage = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append("fileKey", file, file.name);
    const res = await productAPI.uploadProductImages(formData);
    const uploadedFile = res.data.result.files.fileKey[0];
    return categoryAPI.getMediaDownloadUrl
      ? categoryAPI.getMediaDownloadUrl(uploadedFile.name)
      : uploadedFile.name;
  };

  // Fetch product data if editing
  useEffect(() => {
    // Always attempt to fetch product details when in edit mode.
    // Previously this waited for `activeCategories.length > 0` which could
    // prevent the GET from running if categories failed to load — causing
    // the edit flow to not fetch the product. Fetching product immediately
    // allows the page to populate and then fill category/subcategory fields
    // once category data arrives.
    if (isEditMode && editProductId) {
      setPageLoading(true);
      productAPI
        .getProduct(editProductId)
        .then((response: any) => {
          const product = response.data;
          if (!product) {
            toast.error("Product not found");
            setPageLoading(false);
            return;
          }
          // Fill form fields
          let parsedDetails = product.productdetails;
          if (typeof parsedDetails === "string") {
            try {
              parsedDetails = JSON.parse(parsedDetails);
            } catch (e) {
              parsedDetails = [];
            }
          }

          // Prefill category/subcategory logic based on category object
          let prefillCategory = "";
          let prefillSubcategory = "";
          if (product.category && product.category.parentId) {
            prefillCategory = product.category.parentId;
            prefillSubcategory = product.category.id;
          } else if (product.category && !product.category.parentId) {
            prefillCategory = product.category.id;
            prefillSubcategory = "";
          } else {
            if (product.subcategoryId) {
              prefillCategory = product.categoryId || "";
              prefillSubcategory = product.subcategoryId;
            } else {
              prefillCategory = product.categoryId || "";
              prefillSubcategory = "";
            }
          }

          // Get groupPoints from Redux state product, default to "0" for missing
          let groupPointsFromState: { [key: string]: string } = {};
          const reduxProduct = products.find(
            (p: any) => p.id === editProductId,
          );
          if (reduxProduct && Array.isArray(reduxProduct.groupprices)) {
            // Map existing group prices by group name
            reduxProduct.groupprices.forEach((gp: any) => {
              const groupName = gp.group?.name || gp.groupId || "";
              groupPointsFromState[groupName] = gp.newprice?.toString() || "0";
            });
          }
          // Ensure every group has a default key (0) when groupsList is available
          groupsList.forEach((group: any) => {
            if (typeof groupPointsFromState[group.name] === "undefined") {
              groupPointsFromState[group.name] = "0";
            }
          });

          reset({
            name: product.name || "",
            points: product.price?.toString() || "",
            category: prefillCategory,
            subcategory: prefillSubcategory,
            quantity: product.availablequantity?.toString() || "",
            minOrderQuantity: product.minimumorderquantity?.toString() || "",
            description: product.description || "",
            productDetails: parsedDetails || {},
            groupPoints: groupPointsFromState,
            productstatus:
              typeof product.productstatus !== "undefined"
                ? String(product.productstatus) === "0"
                  ? "0"
                  : "1"
                : "1",
          });

          if (prefillSubcategory) {
            categoryAPI
              .getSubcategories(prefillCategory)
              .then((response: any) => {
                const subcategoriesData =
                  response.data?.data || response.data || [];
                setSubcategories(subcategoriesData);
                setShowSubcategory(true);
                setTimeout(() => {
                  setValue("subcategory", prefillSubcategory);
                }, 0);
              });
          }
          if (parsedDetails && Array.isArray(parsedDetails)) {
            setProductDetailFields(
              parsedDetails.map((detail: any, idx: number) => ({
                id: `edit-${idx}`,
                label: detail.lable,
                value: detail.data,
                isDefault: true,
              })),
            );
          }
          if (product.productmedia && Array.isArray(product.productmedia)) {
            const imageUrls = product.productmedia
              .map((media: any) => {
                const url = media.productname
                  ? categoryAPI.getMediaDownloadUrl(media.productname)
                  : null;
                return url;
              })
              .filter((url: string | null) => !!url);
            setImagePreviews(imageUrls);
            setSelectedFiles([]);
            const backendNames = product.productmedia
              .map((media: any) => media.productname)
              .filter((name: string | null) => !!name);
            setExistingImageNames(backendNames);
          }

          // No explicit "selected groups" state needed anymore — all groups
          // are displayed with an input. Values are taken from `groupPoints`.

          setPageLoading(false);
        })
        .catch((error: any) => {
          toast.error("Failed to fetch product details");
          setPageLoading(false);
        });
    }
  }, [
    isEditMode,
    editProductId,
    reset,
    activeCategories,
    groupsList,
    setValue,
    products,
  ]);

  const watchedCategory = watch("category");

  // NOTE: Automatic propagation of the main `points` value to all group
  // inputs has been disabled. Per-group points must be set explicitly
  // by the user to avoid unintentionally assigning points to all groups.

  // Load initial data when component mounts
  useEffect(() => {
    const loadInitialData = async () => {
      setPageLoading(true);
      try {
        // Call all required APIs in parallel
        const params: any = { where: {} };
        params["where[status]"] = 1;
        const [categoriesResponse, productMasterResponse, groupsResponse] =
          await Promise.all([
            categoryAPI.getActiveCategories(),
            productDetailsAPI.getProductDetailsLabels(),
            groupsAPI.getGroups(params),
          ]);

        // Set the data based on actual API response structures
        // Categories: response.data.data is the array (response has data and length properties)
        setActiveCategories(
          categoriesResponse.data?.data || categoriesResponse.data || [],
        );

        // Product Details: response.data.product_details is the array
        // setProductMasterDetails(productMasterResponse.data?.product_details || []); // This line is removed

        // Groups: response.data.data is the array (response has data and length properties)
        setGroupsList(groupsResponse.data?.data || groupsResponse.data || []);

        // Initialize product details with empty values
        const initialProductDetails: { [key: string]: string } = {};
        const initialGroupPoints: { [key: string]: string } = {};

        // Initialize product detail fields with API data
        const initialProductDetailFields = (
          productMasterResponse.data?.product_details || []
        ).map((detail: ProductDetail) => ({
          id: `api-${detail.id}`,
          label: detail.lable,
          value: "",
          isDefault: true,
        }));

        setProductDetailFields(initialProductDetailFields);

        (productMasterResponse.data?.product_details || []).forEach(
          (detail: ProductDetail) => {
            initialProductDetails[detail.lable] = "";
          },
        );

        // Handle groups data properly - check for nested data structure
        const groupsData =
          groupsResponse.data?.data || groupsResponse.data || [];
        groupsData.forEach((group: Group) => {
          // Default to "0" so inputs show 0 when no group price exists
          initialGroupPoints[group.name] = "0";
        });

        setValue("productDetails", initialProductDetails);
        setValue("groupPoints", initialGroupPoints);

        console.log("Loaded data:", {
          categories: categoriesResponse.data,
          productMaster: productMasterResponse.data,
          groups: groupsResponse.data,
        });
      } catch (error) {
        console.error("Error loading initial data:", error);
        toast.error("Failed to load initial data. Please refresh the page.");
      } finally {
        setPageLoading(false);
      }
    };

    loadInitialData();
  }, [setValue]);

  // Load subcategories when category changes
  useEffect(() => {
    const loadSubcategories = async () => {
      if (watchedCategory) {
        try {
          // Always call the subcategory API when category changes
          const response = await categoryAPI.getSubcategories(watchedCategory);
          const subcategoriesData = response.data?.data || response.data || [];

          if (subcategoriesData.length > 0) {
            setSubcategories(subcategoriesData);
            setShowSubcategory(true);
            // Clear subcategory selection when category changes
            setValue("subcategory", "");
          } else {
            setSubcategories([]);
            setShowSubcategory(false);
            setValue("subcategory", "");
          }
        } catch (error) {
          console.error("Error loading subcategories:", error);
          setSubcategories([]);
          setShowSubcategory(false);
          setValue("subcategory", "");
        }
      } else {
        setSubcategories([]);
        setShowSubcategory(false);
        setValue("subcategory", "");
      }
    };

    loadSubcategories();
  }, [watchedCategory, setValue]);

  const handleSubmitForm = async (data: any) => {
    // Group points are optional per-group; we'll only send groups with > 0
    // Clear any previous group error and continue.
    setGroupError(null);
    // Validate image upload
    if (imagePreviews.length === 0) {
      setImageError("Please upload at least one product image.");
      return;
    } else {
      setImageError(null);
    }
    // Custom validation for product name
    const nameValue = data.name.trim();
    let nameError = "";
    if (!nameValue) {
      nameError = "Product name is required";
    } else if (nameValue.length < 3) {
      nameError = "Name must be at least 3 characters";
    } else if (nameValue.length > 50) {
      nameError = "Name must be less than 50 characters";
    } else if (!/^[a-zA-Z0-9\s\-&']+$/.test(nameValue)) {
      nameError =
        "Product name can only contain letters, numbers, spaces, hyphens (-), ampersands (&), and apostrophes (')";
    }

    setProductNameError(nameError);
    // Custom validation for dynamic product detail fields
    const detailErrors: { [id: string]: string } = {};
    productDetailFields.forEach((field) => {
      const labelValue = field.label.trim();
      // Only validate label for new fields (isDefault === false)
      if (!field.isDefault) {
        if (!labelValue) {
          detailErrors[field.id] = "Label is required";
        } else if (labelValue.length < 3) {
          detailErrors[field.id] = "Label must be at least 3 characters";
        } else if (labelValue.length > 50) {
          detailErrors[field.id] = "Label must be less than 50 characters";
        } else if (!/^[a-zA-Z\s]+$/.test(labelValue)) {
          detailErrors[field.id] =
            "Label can only contain alphabets and spaces";
        }
      }
    });
    setProductDetailErrors(detailErrors);
    // Prevent submit if any errors
    if (nameError || Object.keys(detailErrors).length > 0) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      // Step 1: Upload images first if any are selected
      let uploadedImages = [];
      if (selectedFiles.length > 0) {
        for (let i = 0; i < selectedFiles.length; i++) {
          const file = selectedFiles[i];
          const formData = new FormData();
          formData.append("fileKey", file, file.name);
          const uploadResponse = await productAPI.uploadProductImages(formData);
          const uploadedFile = uploadResponse.data.result.files.fileKey[0];
          uploadedImages.push({
            name: uploadedFile.name,
            sequence: new Date(Date.now() + i * 1000).toISOString(),
          });
        }
      }

      // Step 1b: Get existing images in edit mode, include id only for old images
      let existingImages: Array<{
        name: string;
        sequence: string;
        id?: string;
      }> = [];
      let productFromState: any = null;
      if (isEditMode && editProductId) {
        productFromState = products.find((p: any) => p.id === editProductId);
      }
      if (
        isEditMode &&
        existingImageNames.length > 0 &&
        productFromState &&
        Array.isArray(productFromState.productmedia)
      ) {
        existingImages = existingImageNames.map((name: string, idx: number) => {
          const found = productFromState.productmedia.find(
            (media: any) => media.productname === name,
          );
          if (found && found.id) {
            return {
              name,
              sequence: new Date(Date.now() + idx * 1000).toISOString(),
              id: found.id,
            };
          } else {
            return {
              name,
              sequence: new Date(Date.now() + idx * 1000).toISOString(),
            };
          }
        });
      }

      // Step 2: Prepare product details array
      const productDetailsArray = productDetailFields
        .filter(
          (field) => field.label.trim() !== "" && field.value.trim() !== "",
        )
        .map((field, index) => ({
          id: index + 1,
          lable: field.label,
          data: field.value,
        }));

      // Step 3: Prepare group prices array, include id if available
      let groupPricesArray = groupsList
        .filter((group) => {
          const raw = data.groupPoints?.[group.name];
          const val =
            raw === "" || typeof raw === "undefined" ? 0 : Number(raw);
          return !isNaN(val) && val > 0;
        })
        .map((group) => {
          let id;
          if (
            isEditMode &&
            productFromState &&
            Array.isArray(productFromState.groupprices)
          ) {
            const found = productFromState.groupprices.find(
              (gp: any) => gp.groupId === group.id,
            );
            id = found ? found.id : undefined;
          }
          return {
            group_id: group.id,
            groupprice: parseFloat(data.groupPoints[group.name]),
            id,
          };
        });

      // Step 4: Determine categoryId (subcategory takes precedence). Use null when none selected.
      const categoryId = data.subcategory || data.category || null;

      // Step 5: Prepare the complete request body
      // Only pass id for old images, not for new uploads
      const productmediaArray = [
        ...existingImages,
        ...uploadedImages.map((img) => ({
          name: img.name,
          sequence: img.sequence,
        })),
      ];
      const requestBody = {
        name: data.name.trim(),
        description: data.description.trim(),
        price: parseFloat(data.points),
        availablequantity: parseInt(data.quantity),
        minimumorderquantity: parseInt(data.minOrderQuantity),
        categoryId: categoryId,
        productstatus: parseInt(data.productstatus),
        productdetails: productDetailsArray,
        productmedia: productmediaArray,
        mainGroupPrice: groupPricesArray,
        productvariation: [],
        variationconfig: [],
        productunit: "",
        videourllink: "",
        productbrochure: "",
        other: null,
        producttypeId: 1,
        inInquiry: false,
        sellcounter: 0,
        expecteddays: 7,
      };
      if (isEditMode && editProductId) {
        await productAPI.updateProduct(editProductId, {
          ...requestBody,
          deletedproductmedia: deletedProductMedia,
        });
        toast.success("Product updated successfully!");
      } else {
        await productAPI.createProduct(requestBody);
        toast.success("Product added successfully!");
      }
      navigate("/products");
    } catch (error: any) {
      console.error(
        isEditMode ? "Error updating product:" : "Error adding product:",
        error,
      );
      let errorMessage = isEditMode
        ? "Failed to update product. Please try again."
        : "Failed to add product. Please try again.";

      if (error.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleAddProductDetail = () => {
    const newField = {
      id: `dynamic-${Date.now()}`,
      label: "New Field",
      value: "",
      isDefault: false,
    };
    setProductDetailFields((prev) => [...prev, newField]);
  };

  const handleRemoveProductDetail = (id: string) => {
    setProductDetailFields((prev) => prev.filter((field) => field.id !== id));
  };

  const handleDynamicFieldChange = (
    id: string,
    field: "label" | "value",
    value: string,
  ) => {
    setProductDetailFields((prev) =>
      prev.map((item) => (item.id === id ? { ...item, [field]: value } : item)),
    );
  };

  const handleBack = () => {
    navigate("/products");
  };

  const handleFileSelect = (files: FileList | null) => {
    if (files) {
      const newFiles = Array.from(files);

      // Validate file types and sizes
      const validFiles = newFiles.filter((file) => {
        const isValidType =
          file.type === "image/jpeg" ||
          file.type === "image/jpg" ||
          file.type === "image/png";
        const maxSize = 2 * 1024 * 1024; // 2MB
        const isValidSize = file.size <= maxSize;

        if (!isValidType) {
          toast.error(`Only JPEG/JPG/PNG files are allowed.`);
        } else if (!isValidSize) {
          toast.error(`File size must be less than 2MB.`);
        }

        return isValidType && isValidSize;
      });

      // Limit total images to 5
      const totalImages = selectedFiles.length + validFiles.length;
      if (totalImages > 5) {
        toast.error("You can upload a maximum of 5 images.");
        validFiles.splice(5 - selectedFiles.length); // Only allow up to 5
      }

      if (validFiles.length > 0) {
        setSelectedFiles((prev) => [...prev, ...validFiles].slice(0, 5)); // Ensure max 5
        validFiles.forEach((file) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            setImagePreviews((prev) =>
              [...prev, e.target?.result as string].slice(0, 5),
            );
          };
          reader.readAsDataURL(file);
        });
      }
    }
  };

  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    handleFileSelect(event.target.files);
    // Always reset input so same file can be selected again
    event.target.value = "";
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Helper: which tab has errors
  const tabErrors = [
    !!(
      errors.name ||
      errors.points ||
      errors.quantity ||
      errors.minOrderQuantity ||
      errors.description ||
      productNameError ||
      Object.keys(productDetailErrors).length > 0
    ),
    // Tab 2: show error only if there are groupPoints validation errors
    (() => {
      const groupPointsErrors = errors.groupPoints as
        | Record<string, any>
        | undefined;
      return !!groupPointsErrors && Object.keys(groupPointsErrors).length > 0;
    })(),
  ];

  if (pageLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      {/* KEEP GlobalStyles that are unrelated to removed local editor styling if still needed.
          You can optionally delete the image alignment rules here because RichTextEditor injects its own. */}
      <GlobalStyles
        styles={{
          ".ProseMirror img": {
            maxWidth: "100%",
            height: "auto",
            cursor: "pointer",
            display: "block",
          },
          '.ProseMirror img[data-align="left"]': {
            margin: "4px 16px 8px 0",
            float: "left",
            clear: "both",
          },
          '.ProseMirror img[data-align="right"]': {
            margin: "4px 0 8px 16px",
            float: "right",
            clear: "both",
          },
          '.ProseMirror img[data-align="center"]': {
            margin: "8px auto",
            float: "none",
          },
          '.ProseMirror img:not([data-align]), .ProseMirror img[data-align="null"]':
            {
              margin: "8px 0",
              float: "none",
            },
          ".ProseMirror-selectednode": {
            outline: "2px solid #673ab7",
            outlineOffset: "2px",
          },
          ".ProseMirror p::after": {
            content: '""',
            display: "block",
            clear: "both",
          },
        }}
      />
      <Box
        sx={{
          minHeight: "100vh",
          background: "white",
          py: 3,
          px: 3,
          position: "relative",
        }}
      >
        {/* Loader overlay for saving */}
        {loading && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              bgcolor: "rgba(255,255,255,0.7)",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography sx={{ mr: 2, fontWeight: 600, color: "primary.main" }}>
              Saving...
            </Typography>
            <CircularProgress color="primary" />
          </Box>
        )}

        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            maxWidth: "1400px",
            mx: "auto",
            mb: 2,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 800,
              color: "#333",
              fontSize: "1.25rem",
              letterSpacing: 0.4,
            }}
          >
            {isEditMode ? "Edit Product" : "Add New Product"}
          </Typography>
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              sx={{
                background: "white",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
                px: 2,
                py: 0.5,
                minHeight: "32px",
                fontSize: "0.95rem",
                boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
                "&:hover": {
                  background: "#f5f5f5",
                },
                transition: "all 0.2s ease",
              }}
            >
              Back to Products
            </Button>
            <Button
              variant="outlined"
              onClick={handleBack}
              disabled={loading}
              sx={{
                borderColor: "primary.main",
                color: "primary.main",
                px: 2,
                py: 0.5,
                minHeight: "32px",
                borderRadius: 2,
                fontWeight: 600,
                fontSize: "0.95rem",
                boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
                "&:hover": {
                  bgcolor: "primary.main",
                  borderColor: "transparent",
                  color: "white",
                },
                transition: "all 0.3s ease",
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={
                loading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <SaveIcon sx={{ fontSize: 18 }} />
                )
              }
              disabled={loading}
              sx={{
                bgcolor: "secondary.main",
                px: 2,
                py: 0.5,
                minHeight: "32px",
                fontWeight: 600,
                fontSize: "0.95rem",
                borderRadius: 2,
                boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
                "&:hover": {
                  bgcolor: "primary.main",
                },
                "&:disabled": {
                  background: "#ccc",
                  transform: "none",
                  boxShadow: "none",
                },
                transition: "all 0.3s ease",
              }}
              onClick={() => {
                handleSubmit(handleSubmitForm)();
              }}
            >
              {loading
                ? isEditMode
                  ? "Updating..."
                  : "Saving..."
                : isEditMode
                  ? "Update"
                  : "Save"}
            </Button>
          </Box>
        </Box>

        {/* Divider after header */}
        <Divider sx={{ mb: 3, maxWidth: "1400px", mx: "auto" }} />

        {/* Tabs */}
        <Box sx={{ maxWidth: "1400px", mx: "auto", mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
            sx={{
              bgcolor: "#fff",
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
              mb: 2,
            }}
          >
            <Tab
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  Basic Info
                  {tabErrors[0] && (
                    <ErrorOutlineIcon color="error" fontSize="small" />
                  )}
                </Box>
              }
              sx={{ fontWeight: 700, fontSize: "1rem" }}
            />
            <Tab
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  Manage Groups Wise Points
                  {tabErrors[1] && (
                    <ErrorOutlineIcon color="error" fontSize="small" />
                  )}
                </Box>
              }
              sx={{ fontWeight: 700, fontSize: "1rem" }}
            />
          </Tabs>
        </Box>

        <form id="add-product-form" onSubmit={handleSubmit(handleSubmitForm)}>
          {/* Tab 1: Basic Info */}
          {activeTab === 0 && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 4,
                maxWidth: "1400px",
                mx: "auto",
              }}
            >
              {/* Row 1: General Details + Upload Images */}
              <Box
                sx={{
                  display: "flex",
                  gap: 3,
                  flexDirection: { xs: "column", xl: "row" },
                }}
              >
                {/* Upload Product Images */}
                <Card
                  sx={{
                    background: "white",
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                    boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                    minWidth: { xs: "100%", xl: "340px" },
                    flex: 1,
                    mb: { xs: 2, xl: 0 },
                  }}
                >
                  <Box
                    sx={{
                      background: "#f8f9fa",
                      p: 1.5,
                      borderBottom: "1px solid #e0e0e0",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 800,
                        color: "primary.main",
                        fontSize: ".95rem",
                        letterSpacing: 0.5,
                      }}
                    >
                      Upload Product Images
                    </Typography>
                  </Box>
                  <CardContent
                    sx={{
                      p: 2,
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        flex: 1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="primary.main"
                        sx={{ mb: 1.5, fontWeight: 400, fontSize: "0.85rem" }}
                      >
                        Product Images <span style={{ color: "red" }}>*</span>{" "}
                        &nbsp; Upload JPEG/JPG/PNG files only (max 2MB each)
                      </Typography>
                      {imagePreviews.length === 0 && imageError && (
                        <Typography
                          variant="body2"
                          color="error"
                          sx={{ fontWeight: 600, mb: 1 }}
                        >
                          {imageError}
                        </Typography>
                      )}

                      {/* Hidden file input */}
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept="image/jpeg,image/jpg,image/png"
                        onChange={handleFileInputChange}
                        style={{ display: "none" }}
                      />

                      {/* Upload area */}
                      <Box
                        onClick={handleUploadClick}
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                        sx={{
                          border: dragActive
                            ? "2px dashed primary.main"
                            : "2px dashed #ccc",
                          borderRadius: 2,
                          p: 3,
                          textAlign: "center",
                          cursor: "pointer",
                          background: dragActive ? "#f0f8ff" : "#f8f9fa",
                          transition: "all 0.2s ease",
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          "&:hover": {
                            borderColor: "primary.main",
                            background: "background.default",
                          },
                        }}
                      >
                        <Typography
                          variant="body1"
                          color="primary"
                          sx={{
                            fontWeight: 500,
                            mb: 0.5,
                            fontSize: "0.9rem",
                          }}
                        >
                          Choose File
                        </Typography>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{ fontSize: "0.8rem" }}
                        >
                          Drag and drop files here or click to browse
                        </Typography>
                      </Box>

                      {/* Selected files display */}
                      {/* Always show image previews, including backend URLs in edit mode */}
                      {imagePreviews.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              mb: 1.5,
                              fontSize: "0.85rem",
                            }}
                          >
                            Product Images{" "}
                            <span style={{ color: "red" }}>*</span> (
                            {imagePreviews.length}):
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 1.5,
                              maxHeight: "200px",
                              overflow: "auto",
                            }}
                          >
                            {imagePreviews.map((url, index) => (
                              <Box
                                key={index}
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  gap: 0.5,
                                  p: 1,
                                  borderRadius: 1,
                                  background: "#f8f9fa",
                                  border: "1px solid #e0e0e0",
                                  minWidth: "80px",
                                }}
                              >
                                {/* Image Preview */}
                                <Box
                                  sx={{
                                    width: 60,
                                    height: 60,
                                    borderRadius: 1,
                                    overflow: "hidden",
                                    border: "1px solid #e0e0e0",
                                    flexShrink: 0,
                                    cursor: "pointer",
                                    position: "relative",
                                    "&:hover": {
                                      borderColor: "primary.main",
                                      "& .zoom-overlay": {
                                        opacity: 1,
                                      },
                                    },
                                  }}
                                  onClick={() =>
                                    setSelectedImage({
                                      url,
                                      index: index + 1,
                                    })
                                  }
                                >
                                  <img
                                    src={url}
                                    alt={`Product Preview ${index + 1}`}
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                    }}
                                  />
                                  {/* Zoom overlay */}
                                  <Box
                                    className="zoom-overlay"
                                    sx={{
                                      position: "absolute",
                                      top: 0,
                                      left: 0,
                                      right: 0,
                                      bottom: 0,
                                      backgroundColor: "rgba(0,0,0,0.3)",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      opacity: 0,
                                      transition: "opacity 0.2s ease",
                                    }}
                                  >
                                    <Box
                                      component="span"
                                      sx={{
                                        color: "white",
                                        fontSize: "1rem",
                                        fontWeight: "bold",
                                      }}
                                    >
                                      🔍
                                    </Box>
                                  </Box>
                                  {/* Image sequence number */}
                                  <Box
                                    sx={{
                                      position: "absolute",
                                      top: 2,
                                      right: 2,
                                      backgroundColor: "rgba(0,0,0,0.7)",
                                      color: "white",
                                      borderRadius: "50%",
                                      width: "16px",
                                      height: "16px",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      fontSize: "0.6rem",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    {index + 1}
                                  </Box>
                                </Box>
                                {/* Remove Button */}
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => {
                                    setImagePreviews((prev) =>
                                      prev.filter((_, i) => i !== index),
                                    );
                                    setSelectedFiles((prev) =>
                                      prev.filter((_, i) => i !== index),
                                    );
                                    // If in edit mode and existing image, track for deletion
                                    if (isEditMode) {
                                      const removedImageName =
                                        existingImageNames[index];
                                      if (removedImageName) {
                                        setDeletedProductMedia((prev) => [
                                          ...prev,
                                          removedImageName,
                                        ]);
                                        setExistingImageNames((prev) =>
                                          prev.filter((_, i) => i !== index),
                                        );
                                      }
                                    }
                                  }}
                                  sx={{
                                    background: "error.light",
                                    width: "24px",
                                    height: "24px",
                                    mt: 0.5,
                                    "&:hover": {
                                      background: "error.main ",
                                    },
                                  }}
                                >
                                  <DeleteIcon sx={{ fontSize: "0.8rem" }} />
                                </IconButton>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>

                {/* General Details Section */}
                <Card
                  sx={{
                    background: "white",
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                    boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                    flex: 2,
                    minWidth: 0,
                  }}
                >
                  <Box
                    sx={{
                      background: "#f8f9fa",
                      p: 1.5,
                      borderBottom: "1px solid #e0e0e0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 800,
                        color: "primary.main",
                        fontSize: ".95rem",
                        letterSpacing: 0.5,
                      }}
                    >
                      Product Information
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <Link
                        component="button"
                        underline="hover"
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.98rem",
                          color: "primary.main",
                          p: 0,
                          minWidth: 0,
                        }}
                        onClick={() => setOpenCategoryDialog(true)}
                      >
                        Add Category
                      </Link>
                      {showSubcategory && (
                        <Link
                          component="button"
                          underline="hover"
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.98rem",
                            color: "primary.main",
                            p: 0,
                            minWidth: 0,
                          }}
                          onClick={() => setOpenSubcategoryDialog(true)}
                        >
                          Add Subcategory
                        </Link>
                      )}
                    </Box>
                  </Box>
                  <CardContent
                    sx={{
                      p: 2,
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        flex: 1,
                      }}
                    >
                      {/* First Row: Product Name, Points, Category */}
                      <Box
                        sx={{
                          display: "flex",
                          gap: 2,
                          flexDirection: { xs: "column", lg: "row" },
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <TextField
                            fullWidth
                            label="Product Name *"
                            InputLabelProps={{
                              sx: {
                                fontSize: ".65rem",
                                textTransform: "uppercase",
                                letterSpacing: 0.6,
                                fontWeight: 600,
                              },
                            }}
                            error={!!errors.name || !!productNameError}
                            helperText={
                              productNameError || errors.name?.message
                            }
                            {...register("name")}
                            size="small"
                          />
                        </Box>
                      </Box>

                      {/* Second Row: Available Quantity, Minimum Order Quantity */}
                      <Box
                        sx={{
                          display: "flex",
                          gap: 2,
                          flexDirection: { xs: "column", lg: "row" },
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <TextField
                            fullWidth
                            label="Points *"
                            type="number"
                            InputLabelProps={{
                              sx: {
                                fontSize: ".65rem",
                                textTransform: "uppercase",
                                letterSpacing: 0.6,
                                fontWeight: 600,
                              },
                            }}
                            error={!!errors.points}
                            helperText={errors.points?.message}
                            {...register("points")}
                            size="small"
                          />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Controller
                            name="category"
                            control={control}
                            render={({ field }) => (
                              <FormControl
                                fullWidth
                                error={!!errors.category}
                                size="small"
                              >
                                <InputLabel
                                  sx={{
                                    fontSize: ".65rem",
                                    textTransform: "uppercase",
                                    letterSpacing: 0.6,
                                    fontWeight: 600,
                                  }}
                                >
                                  Select Category
                                </InputLabel>
                                <Select {...field} label="Select Category *">
                                  {activeCategories.map(
                                    (category: Category) => (
                                      <MenuItem
                                        key={category.id}
                                        value={category.id}
                                      >
                                        {category.name}
                                      </MenuItem>
                                    ),
                                  )}
                                </Select>
                                <FormHelperText>
                                  {errors.category?.message}
                                </FormHelperText>
                              </FormControl>
                            )}
                          />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <TextField
                            fullWidth
                            label="Available Quantity *"
                            type="number"
                            InputLabelProps={{
                              sx: {
                                fontSize: ".65rem",
                                textTransform: "uppercase",
                                letterSpacing: 0.6,
                                fontWeight: 600,
                              },
                            }}
                            error={!!errors.quantity}
                            helperText={errors.quantity?.message}
                            {...register("quantity")}
                            size="small"
                          />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <TextField
                            fullWidth
                            label="Minimum Order Quantity *"
                            type="number"
                            InputLabelProps={{
                              sx: {
                                fontSize: ".65rem",
                                letterSpacing: 0.6,
                                fontWeight: 600,
                              },
                            }}
                            error={!!errors.minOrderQuantity}
                            helperText={errors.minOrderQuantity?.message}
                            {...register("minOrderQuantity")}
                            size="small"
                          />
                        </Box>
                      </Box>

                      {/* Subcategory Row - Only show when needed */}
                      {showSubcategory && (
                        <Box
                          sx={{
                            display: "flex",
                            gap: 2,
                            flexDirection: { xs: "column", lg: "row" },
                          }}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Controller
                              name="subcategory"
                              control={control}
                              render={({ field }) => (
                                <FormControl
                                  fullWidth
                                  error={!!errors.subcategory}
                                  size="small"
                                >
                                  <InputLabel
                                    sx={{
                                      fontSize: ".65rem",
                                      textTransform: "uppercase",
                                      letterSpacing: 0.6,
                                      fontWeight: 600,
                                    }}
                                  >
                                    Select Subcategory *
                                  </InputLabel>
                                  <Select
                                    {...field}
                                    label="Select Subcategory *"
                                  >
                                    {subcategories.map(
                                      (subcategory: Category) => (
                                        <MenuItem
                                          key={subcategory.id}
                                          value={subcategory.id}
                                        >
                                          {subcategory.name}
                                        </MenuItem>
                                      ),
                                    )}
                                  </Select>
                                  <FormHelperText>
                                    {errors.subcategory?.message}
                                  </FormHelperText>
                                </FormControl>
                              )}
                            />
                          </Box>
                        </Box>
                      )}

                      {/* <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', lg: 'row' } }}> */}
                      <Box sx={{ flex: 1 }}>
                        <Controller
                          name="productstatus"
                          control={control}
                          render={({ field }) => (
                            <FormControl
                              component="fieldset"
                              error={!!errors.productstatus}
                              size="small"
                            >
                              <FormLabel
                                component="legend"
                                sx={{
                                  fontSize: ".65rem",
                                  textTransform: "uppercase",
                                  letterSpacing: 0.6,
                                  fontWeight: 600,
                                }}
                              >
                                Product Status *
                              </FormLabel>
                              <RadioGroup
                                row
                                value={field.value}
                                onChange={field.onChange}
                              >
                                <FormControlLabel
                                  value="1"
                                  control={<Radio />}
                                  label="Active"
                                />
                                <FormControlLabel
                                  value="0"
                                  control={<Radio />}
                                  label="Inactive"
                                />
                              </RadioGroup>
                              <FormHelperText>
                                {errors.productstatus?.message}
                              </FormHelperText>
                            </FormControl>
                          )}
                        />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>

              {/* Divider between rows */}
              <Divider sx={{ my: 2 }} />

              {/* Row 2: Product Details + Product Description */}
              <Box
                sx={{
                  display: "flex",
                  gap: 3,
                  flexDirection: { xs: "column", md: "row" },
                }}
              >
                {/* Product Details Section */}
                <Card
                  sx={{
                    background: "white",
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                    boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  <Box
                    sx={{
                      background: "#f8f9fa",
                      p: 1.5,
                      borderBottom: "1px solid #e0e0e0",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 800,
                        color: "primary.main",
                        fontSize: ".95rem",
                        letterSpacing: 0.5,
                      }}
                    >
                      Product Details
                    </Typography>
                  </Box>
                  <CardContent
                    sx={{
                      p: 2,
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        flex: 1,
                      }}
                    >
                      {/* Dynamic product detail fields */}
                      {productDetailFields.map((field, idx) => (
                        <Box
                          key={field.id}
                          sx={{
                            display: "flex",
                            gap: 2,
                            alignItems: "center",
                            mb: 1,
                          }}
                        >
                          <TextField
                            label="Label"
                            value={field.label}
                            onChange={(e) =>
                              handleDynamicFieldChange(
                                field.id,
                                "label",
                                e.target.value,
                              )
                            }
                            size="small"
                            sx={{ flex: 2 }}
                            error={!!productDetailErrors[field.id]}
                            helperText={productDetailErrors[field.id]}
                          />
                          <TextField
                            label="Value"
                            value={field.value}
                            onChange={(e) =>
                              handleDynamicFieldChange(
                                field.id,
                                "value",
                                e.target.value,
                              )
                            }
                            size="small"
                            sx={{ flex: 3 }}
                          />
                          {!field.isDefault && (
                            <IconButton
                              color="error"
                              onClick={() =>
                                handleRemoveProductDetail(field.id)
                              }
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </Box>
                      ))}
                      <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={handleAddProductDetail}
                        sx={{ mt: 1, fontWeight: 600 }}
                      >
                        Add Product Detail
                      </Button>
                    </Box>
                  </CardContent>
                </Card>

                {/* Product Description Section */}
                <Card
                  sx={{
                    background: "white",
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                    boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  <Box
                    sx={{
                      background: "#f8f9fa",
                      p: 1.5,
                      borderBottom: "1px solid #e0e0e0",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 800,
                        color: "primary.main",
                        fontSize: ".95rem",
                        letterSpacing: 0.5,
                      }}
                    >
                      Product Description<span style={{ color: "red" }}>*</span>
                    </Typography>
                  </Box>
                  <CardContent
                    sx={{
                      p: 2,
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                    }}
                  >
                    <RichTextEditor
                      value={watch("description")}
                      onChange={(html) =>
                        setValue("description", html, { shouldValidate: true })
                      }
                      maxChars={5000}
                      uploadImage={uploadDescriptionImage}
                      error={!!errors.description}
                      helperText={errors.description?.message}
                      label={null}
                      placeholder="Enter product description..."
                    />
                  </CardContent>
                </Card>
              </Box>
            </Box>
          )}
          {/* Tab 2: Group Info */}
          {activeTab === 1 && (
            <Box sx={{ width: "100%", maxWidth: "1400px", mx: "auto", mt: 2 }}>
              <Card
                sx={{
                  background: "white",
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                  boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                  p: 3,
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 800, color: "primary.main" }}
                  >
                    Group Points
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenGroupDialog(true)}
                    sx={{ fontWeight: 600 }}
                  >
                    Add Group
                  </Button>
                </Box>
                <Divider sx={{ mb: 2 }} />

                {/* List all groups with points input (defaults to 0) */}
                {groupError && (
                  <Typography
                    variant="body2"
                    color="error"
                    sx={{ fontWeight: 600, mt: 1 }}
                  >
                    {groupError}
                  </Typography>
                )}

                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ flex: 1, fontWeight: 700, color: "primary.main" }}
                  >
                    Group Points
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    mb: 2,
                  }}
                >
                  {groupsList.length === 0 ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        px: 2,
                        py: 2,
                        textAlign: "center",
                        bgcolor: "#f8f9fa",
                        borderRadius: 2,
                        fontWeight: 600,
                      }}
                    >
                      No groups found
                    </Typography>
                  ) : (
                    groupsList.map((group) => (
                      <Box
                        key={group.id}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          borderRadius: 2,
                          px: 2,
                          py: 1,
                          gap: 2,
                          width: "100%",
                          minWidth: 0,
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: "primary.main",
                            flex: 2,
                          }}
                        >
                          {group.name}
                        </Typography>
                        <Controller
                          name={`groupPoints.${group.name}`}
                          control={control}
                          rules={{
                            validate: (value) => {
                              if (value === "" || value === undefined)
                                return true;
                              if (isNaN(Number(value)))
                                return "Points must be a valid number";
                              if (Number(value) < 0)
                                return "Points cannot be negative";
                              return true;
                            },
                          }}
                          render={({ field, fieldState }) => (
                            <TextField
                              {...field}
                              size="small"
                              label="Points"
                              placeholder="Enter points"
                              type="number"
                              variant="outlined"
                              sx={{
                                flex: 2,
                                mx: 2,
                                minWidth: 120,
                                "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                                  { WebkitAppearance: "none", margin: 0 },
                                "& input[type=number]": {
                                  MozAppearance: "textfield",
                                },
                              }}
                              error={!!fieldState.error}
                              helperText={fieldState.error?.message}
                              inputProps={{ min: 0, step: 1 }}
                            />
                          )}
                        />
                      </Box>
                    ))
                  )}
                </Box>
              </Card>
            </Box>
          )}
        </form>

        {/* Image Preview Modal */}
        <Dialog
          open={!!selectedImage}
          onClose={() => setSelectedImage(null)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              overflow: "hidden",
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
              backgroundColor: "white",
              maxWidth: "500px",
              maxHeight: "400px",
            },
          }}
        >
          <DialogContent sx={{ p: 0, m: 0, position: "relative" }}>
            <Box
              sx={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "350px",
                p: 2,
              }}
            >
              {/* Close button */}
              <IconButton
                onClick={() => setSelectedImage(null)}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  backgroundColor: "rgba(0,0,0,0.5)",
                  color: "white",
                  zIndex: 10,
                  width: "32px",
                  height: "32px",
                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.7)",
                  },
                }}
              >
                <Box
                  component="span"
                  sx={{ fontSize: "1rem", fontWeight: "bold" }}
                >
                  ×
                </Box>
              </IconButton>

              {/* Previous button */}
              {selectedImage && selectedImage.index > 1 && (
                <IconButton
                  onClick={() => {
                    const prevIndex = selectedImage.index - 1;
                    setSelectedImage({
                      url: imagePreviews[prevIndex - 1],
                      index: prevIndex,
                    });
                  }}
                  sx={{
                    position: "absolute",
                    left: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                    backgroundColor: "rgba(0,0,0,0.5)",
                    color: "white",
                    zIndex: 10,
                    width: "32px",
                    height: "32px",
                    "&:hover": {
                      backgroundColor: "rgba(0,0,0,0.7)",
                    },
                  }}
                >
                  <Box
                    component="span"
                    sx={{ fontSize: "1.2rem", fontWeight: "bold" }}
                  >
                    ‹
                  </Box>
                </IconButton>
              )}

              {/* Next button */}
              {selectedImage && selectedImage.index < selectedFiles.length && (
                <IconButton
                  onClick={() => {
                    const nextIndex = selectedImage.index + 1;
                    setSelectedImage({
                      url: imagePreviews[nextIndex - 1],
                      index: nextIndex,
                    });
                  }}
                  sx={{
                    position: "absolute",
                    right: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                    backgroundColor: "rgba(0,0,0,0.5)",
                    color: "white",
                    zIndex: 10,
                    width: "32px",
                    height: "32px",
                    "&:hover": {
                      backgroundColor: "rgba(0,0,0,0.7)",
                    },
                  }}
                >
                  <Box
                    component="span"
                    sx={{ fontSize: "1.2rem", fontWeight: "bold" }}
                  >
                    ›
                  </Box>
                </IconButton>
              )}

              {/* Image */}
              {selectedImage && (
                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={selectedImage.url}
                    alt={`Product ${selectedImage.index}`}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                    }}
                    onError={(e) => {
                      console.log(
                        "Preview image load error for:",
                        selectedImage.url,
                      );
                    }}
                  />
                </Box>
              )}
            </Box>
          </DialogContent>
        </Dialog>

        {/* Add Category Modal */}
        <CategoryDialog
          open={openCategoryDialog}
          onClose={() => setOpenCategoryDialog(false)}
          mode="add"
          onSave={() => {
            // Reload categories after add
            categoryAPI.getActiveCategories().then((response: any) => {
              setActiveCategories(response.data?.data || response.data || []);
            });
          }}
        />

        {/* Add Subcategory Modal */}
        <SubcategoryDialog
          open={openSubcategoryDialog}
          onClose={() => setOpenSubcategoryDialog(false)}
          mode="add"
          parentId={watchedCategory ?? undefined}
          categoryName={
            activeCategories.find((cat) => cat.id === (watchedCategory ?? ""))
              ?.name
          }
          onSave={() => {
            // Reload subcategories after add
            const parent = watchedCategory;
            if (!parent) {
              setSubcategories([]);
              setShowSubcategory(false);
              return;
            }
            categoryAPI.getSubcategories(parent).then((response: any) => {
              const subcategoriesData =
                response.data?.data || response.data || [];
              setSubcategories(subcategoriesData);
              setShowSubcategory(true);
            });
          }}
        />

        {/* Add Group Modal */}
        <GroupDialog
          open={openGroupDialog}
          onClose={() => {
            setOpenGroupDialog(false);
            // Reload groups after add
            groupsAPI.getGroups().then((response: any) => {
              setGroupsList(response.data?.data || response.data || []);
            });
          }}
          mode="add"
        />
      </Box>
    </>
  );
};

export default AddProduct;
